import { useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { Reveal } from "./Reveal";
import { GALLERY_ITEMS, GALLERY_FILTERS } from "../data/content";

export const Gallery = () => {
  const [filter, setFilter] = useState("te-gjitha");
  const [lightbox, setLightbox] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const items = useMemo(
    () => (filter === "te-gjitha" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.category === filter)),
    [filter]
  );

  const COLLAPSED_COUNT = 9;
  const collapsible = filter === "te-gjitha" && items.length > COLLAPSED_COUNT;
  const visible = collapsible && !expanded ? items.slice(0, COLLAPSED_COUNT) : items;

  const chooseFilter = (key) => {
    setFilter(key);
    setExpanded(false);
  };

  return (
    <section id="punimet" data-testid="gallery-section" className="bg-graphite py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="eyebrow mb-5">Portofoli ynë</p>
              <h2 data-testid="gallery-title" className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Punimet tona
              </h2>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
              {GALLERY_FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`gallery-filter-${f.key}`}
                  onClick={() => chooseFilter(f.key)}
                  className={`border px-4 py-2 font-display text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                    filter === f.key
                      ? "border-bronze bg-bronze text-ink"
                      : "border-white/15 text-white/55 hover:border-bronze/50 hover:text-bronze"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        {items.length === 0 ? (
          <p data-testid="gallery-empty" className="mt-16 border hairline px-6 py-14 text-center text-sm uppercase tracking-[0.25em] text-white/40">
            Fotografi për këtë kategori së shpejti
          </p>
        ) : (
          <div className="mt-14 columns-1 gap-5 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
            {visible.map((item, i) => (
              <Reveal key={item.src} delay={Math.min(i * 0.08, 0.3)} className="mb-5 break-inside-avoid">
                <button
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setLightbox(i)}
                  className="group relative block w-full overflow-hidden text-left"
                >
                  <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                    className={`w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${
                      item.tall ? "aspect-[3/4]" : "aspect-[4/3]"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="absolute inset-x-0 bottom-0 translate-y-3 p-5 opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <p className="font-display text-sm font-semibold text-white">{item.title}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-bronze">Shiko më madhe</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}

        {collapsible && !expanded && (
          <div className="relative -mt-28 flex justify-center bg-gradient-to-t from-graphite via-graphite/85 to-transparent pb-2 pt-28">
            <button
              data-testid="gallery-show-all"
              onClick={() => setExpanded(true)}
              className="group inline-flex items-center gap-3 border border-bronze px-7 py-3.5 font-display text-xs font-semibold uppercase tracking-[0.18em] text-bronze transition-colors duration-300 hover:bg-bronze hover:text-ink"
            >
              Shiko të gjitha fotot
              <ChevronDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </div>
        )}
        {collapsible && expanded && (
          <div className="mt-10 flex justify-center">
            <button
              data-testid="gallery-show-less"
              onClick={() => {
                setExpanded(false);
                document.querySelector("#punimet")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-3 border border-white/20 px-7 py-3.5 font-display text-xs font-semibold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:border-bronze hover:text-bronze"
            >
              Shfaq më pak
              <ChevronUp size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5" />
            </button>
          </div>
        )}
      </div>

      {lightbox !== null && visible[lightbox] && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/95 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close"
            className="absolute right-5 top-5 text-white/70 transition-colors hover:text-bronze"
            onClick={() => setLightbox(null)}
            aria-label="Mbyll"
          >
            <X size={32} />
          </button>
          <button
            data-testid="lightbox-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 p-3 text-white/60 transition-colors hover:text-bronze sm:left-8"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + visible.length) % visible.length); }}
            aria-label="Fotografia e mëparshme"
          >
            <ChevronLeft size={40} />
          </button>
          <figure className="max-h-[85vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <img src={visible[lightbox].src} alt={visible[lightbox].title} className="max-h-[78vh] w-auto max-w-full object-contain" />
            <figcaption data-testid="lightbox-caption" className="mt-4 text-center font-display text-sm text-white/70">
              {visible[lightbox].title}
            </figcaption>
          </figure>
          <button
            data-testid="lightbox-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-3 text-white/60 transition-colors hover:text-bronze sm:right-8"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % visible.length); }}
            aria-label="Fotografia tjetër"
          >
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </section>
  );
};
